/**
 * E2E / chrome fixture bridge — applies Czech long-label overlay when Playwright
 * sets `window.__TP02_CZECH_LONG_LABEL_FIXTURE__` before bootstrap.
 * Does not rewrite product `locales/cs/admin.json`.
 */
import type { i18n as I18nInstance } from 'i18next';
import {
  buildTp02CzechLongLabelI18nOverlay,
  TP02_CZECH_LONG_LABEL_MATRIX,
  TP02_CZECH_LONG_LABEL_WINDOW_KEY,
  type Tp02CzechLongLabelRow,
} from '../ui/fixtures/tp02CzechLongLabelMatrix.js';

type Tp02CzechFixtureWindow = Window & {
  [TP02_CZECH_LONG_LABEL_WINDOW_KEY]?: readonly Tp02CzechLongLabelRow[];
};

function readFixtureRows(): readonly Tp02CzechLongLabelRow[] | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = (window as Tp02CzechFixtureWindow)[TP02_CZECH_LONG_LABEL_WINDOW_KEY];
  if (raw == null || !Array.isArray(raw) || raw.length === 0) {
    return null;
  }
  return raw;
}

/**
 * Merges fixture KPI labels into admin bundles (deep).
 * Applies to `cs` / `en` / `sk` so hermetic runs are not blocked by stored locale.
 * Safe no-op when the window fixture is absent (normal product runs).
 */
export function applyTp02CzechLongLabelFixtureBridge(i18nInstance: I18nInstance): boolean {
  const rows = readFixtureRows() ?? null;
  if (rows === null) {
    return false;
  }

  const overlay = buildTp02CzechLongLabelI18nOverlay(
    rows.length > 0 ? rows : TP02_CZECH_LONG_LABEL_MATRIX,
  );
  for (const lng of ['cs', 'en', 'sk'] as const) {
    i18nInstance.addResourceBundle(lng, 'admin', overlay, true, true);
  }
  return true;
}
